import React, {
  forwardRef,
  type ForwardRefRenderFunction,
  type ButtonHTMLAttributes
} from 'react';
import {
  alignSelf,
  type AlignSelfProps,
  type BackgroundProps,
  background,
  display,
  justifySelf,
  space
} from 'styled-system';
import Icon, { type IconProps } from './icon';
import styled, { css } from 'styled-components';
import { getThemeColor } from '../utils/styles';

export interface IconButtonProps
  extends ButtonHTMLAttributes<HTMLButtonElement>,
    AlignSelfProps,
    BackgroundProps {
  icon: IconProps;
  light?: boolean;
  size?: number;
}

const forwardRefFunction: ForwardRefRenderFunction<
  HTMLButtonElement,
  IconButtonProps
> = ({ icon, ...props }, ref) => (
  <StyledButton {...props} type="button" ref={ref}>
    <Icon {...icon} />
  </StyledButton>
);

type StyledButtonProps = Omit<IconButtonProps, 'icon'>;

const StyledButton = styled.button<StyledButtonProps>`
  ${alignSelf}
  ${justifySelf}
  ${space}
  ${background}
  align-content: center;
  border: none;
  border-radius: 50%;
  cursor: pointer;
  display: grid;
  justify-content: center;
  transition: background-color 0.3s ease-in-out;

  ${display}

  ${(props) => css`
    ${!props.background &&
    css`
      background-color: ${getThemeColor('whiteClear')};
    `}

    height: ${props.size || 48}px;
    width: ${props.size || 48}px;

    ${props.light &&
    css`
      background-color: ${getThemeColor('lightGrey')(props)};
    `}

    &:hover:not([aria-disabled="true"]) {
      background-color: ${getThemeColor('whiteFaded')(props)};

      ${props.light &&
      css`
        background-color: ${getThemeColor('silver')(props)};
      `}
    }
  `}

  svg {
    height: 100%;
    width: 100%;
  }

  &[aria-disabled='true'],
  &:disabled {
    cursor: not-allowed;
    svg {
      opacity: 0.5;
    }
  }
`;

const IconButton = forwardRef(forwardRefFunction);

export default IconButton;
